/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { useForm } from '@mantine/form';

// Styles
import classes from './DrawerStepperOffset.module.css';
import clsx from 'clsx';

// Components
import { Stepper } from '@components/molecules/Stepper';
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { Button } from '@components/atoms/Button';
import { Header } from './Header';

// Components : Steps
import { Step1 } from './Steps/Step1';
import { Step2 } from './Steps/Step2';
import { Step3 } from './Steps/Step3';
import { Step4 } from './Steps/Step4';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import { STEPS } from './DrawerStepperOffset.config';

// Store
import useStore from '@store/index';
import { useOffsetForm } from '@features/offsets/hooks/useOffsetForm';
import { useOffsetProjectMesurability } from '@features/offsets/hooks/useOffsetProjectMesurability';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { useOffsetCreation } from '@features/offsets/hooks/useOffsetCreation';
import { useOffsetProjectAdditional } from '@features/offsets/hooks/useOffsetProjectAdditional';
import { useLocation } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

const ContentOffsets = (props: {
	offsetCreationActiveStep: number;
	form: any;
}) => {
	switch (props.offsetCreationActiveStep) {
		case 0:
			return <Step1 form={props.form} />;
		case 1:
			return <Step2 />;
		case 2:
			return <Step3 />;
		case 3:
			return <Step4 />;

		default:
			break;
	}
};

export const DrawerStepperOffset = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);

	/* ******************************** HOOKS *********************************** */
	const [
		,
		//handleNextStepper,
		handleNextStepButton,
		handlePreviousStepButton,
	] = useOffsetForm();

	const { handleConfirmCreateOffset, isLoading } =
		useOffsetCreation();

	const viewport = useRef<HTMLDivElement>(null);

	const { t } = useTranslation();
	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		setOffsetCreationDatas,
		setOffsetMesurabilityProjectsQueryParams,
		isEnabledOffsetNestStep,
	} = useStore();

	const { handleNextStep: handleNextToStep3 } =
		useOffsetProjectMesurability();

	const { handleNextStep: handleNextToStep4 } =
		useOffsetProjectAdditional();

	// Validators
	const form = useForm({
		initialValues: {
			name: '',
			brand: null,
			quantity: 0,
			startDate: null,
			endDate: null,
			tags: [],
		},
		validate: {
			name: (value) =>
				value.length < 2
					? t('errors.nameMustHaveAtLeast2Letters')
					: null,
			brand: (value) =>
				value == null
					? t('errors.brandIsRequired')
					: null,
			tags: (value) =>
				value.length == 0
					? t('errors.tagsIsRequired')
					: null,
			quantity: (value) =>
				value == 0
					? t('errors.quantityCannotBeZero')
					: null,
			startDate: (value) =>
				value == null
					? t('errors.startDateIsRequired')
					: null,
			endDate: (value) =>
				value == null
					? t('errors.endDateIsRequired')
					: null,
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		if (form.values) {
			setOffsetCreationDatas({
				...form.values,
				selectedMesurabilityProjects: [],
				selectedAdditionalProjects: [],
			});
		}
	}, [form.values]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	useEffect(() => {
		if (props.opened == false) {
			form.reset();
			setOffsetMesurabilityProjectsQueryParams({});
		}
	}, [props.opened]);

	const location = useLocation();

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={
						location.pathname === '/offsets'
							? t('offsets.backToAllOffsetsSure')
							: t('offsets.backToDashboardSure')
					}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t(
							location.pathname === '/offsets'
								? 'offsets.form.backToOffsets'
								: 'offsets.backToDashboard'
						)}
					</PreviousDrawerButton>
					<Box className={classes.containerStepper}>
						<Header />
					</Box>
					{/** Stepper */}
					<Box className={classes.containerStepper}>
						<Stepper
							readOnly
							options={STEPS}
							active={offsetCreationActiveStep}
							iconSize={32}
						/>
					</Box>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<ContentOffsets
								offsetCreationActiveStep={
									offsetCreationActiveStep
								}
								form={form}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.bodyFooter}>
						<Box
							className={classes.containerButtonsFooter}
						>
							<Box
								onClick={() => {
									handlePreviousStepButton();
								}}
								className={clsx(
									classes.previousButton,
									offsetCreationActiveStep == 0 &&
										classes.previousButtonDisabled
								)}
							>
								{t('global.previous')}
							</Box>
						</Box>
						<Box
							className={classes.containerButtonsFooter}
						>
							<Box
								onClick={() => {
									if (location.pathname === '/offsets') {
										setIsOpenModalCancel(true);
									} else {
										props.onClose();
										setIsOpenModalCancel(false);
										form.reset();
									}
								}}
								className={classes.cancelButton}
							>
								{t('global.cancel')}
							</Box>
							<Button
								loading={isLoading}
								disabled={
									offsetCreationActiveStep > 0
										? offsetCreationActiveStep == 4
											? true
											: !isEnabledOffsetNestStep
										: false
								}
								fullWidth
								hiddenFrom="xxl"
								size="md"
								onClick={() => {
									if (offsetCreationActiveStep == 1) {
										handleNextToStep3();
									} else if (
										offsetCreationActiveStep == 2
									) {
										handleNextToStep4();
									} else if (
										offsetCreationActiveStep == 3
									) {
										handleConfirmCreateOffset();
									} else {
										handleNextStepButton(
											form,
											offsetCreationActiveStep
										);
									}
								}}
							>
								{offsetCreationActiveStep == 3
									? t('global.offset')
									: t('global.next')}
							</Button>
							<Button
								loading={isLoading}
								disabled={
									offsetCreationActiveStep > 0
										? offsetCreationActiveStep == 4
											? true
											: !isEnabledOffsetNestStep
										: false
								}
								fullWidth
								visibleFrom="xxl"
								size="lg"
								type="submit"
								onClick={() => {
									if (offsetCreationActiveStep == 1) {
										handleNextToStep3();
									} else if (
										offsetCreationActiveStep == 2
									) {
										handleNextToStep4();
									} else if (
										offsetCreationActiveStep == 3
									) {
										handleConfirmCreateOffset();
									} else {
										handleNextStepButton(
											form,
											offsetCreationActiveStep
										);
									}
								}}
							>
								{offsetCreationActiveStep == 3
									? t('global.offset')
									: t('global.next')}
							</Button>
						</Box>
					</Box>

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
